import { useQuery, useQueryClient } from "@tanstack/react-query";

import { supabase } from "~/integrations/supabase/client";
import { fetchSalesRequestItemsUtil } from "~/pages/purchase-orders/hooks/utils/salesRequestItemsUtils";
import { SalesRequest, SalesRequestItem } from "~/types/client";

/**
 * Custom hook to fetch and cache sales request data
 */
export const useSalesRequest = (salesRequestId: string | null) => {
  return useQuery({
    queryKey: ["salesRequest", salesRequestId],
    queryFn: async (): Promise<SalesRequest> => {
      if (!salesRequestId) {
        throw new Error("Sales request ID is required");
      }

      const { data, error } = await supabase
        .from("sales_request")
        .select("*")
        .eq("id", salesRequestId)
        .single();

      if (error) {
        console.error("Error fetching sales request:", error);
        throw new Error(`Failed to fetch sales request: ${error.message}`);
      }

      // Format the sales request to match the expected structure
      const orderData = {
        id: data.id,
        clientId: data.client_id,
        createdBy: data.created_by,
        approvedRejectedBy: data.approved_rejected_by,
        status: data.status,
        paid: data.paid,
        paymentTerms: data.payment_terms,
        paymentType: data.payment_type,
        paymentDueDate: data.payment_due_date,
        totalAmount: data.total_amount,
        price_with_iva: data.price_with_iva,
        comments: data.comments,
        internal_notes: data.internal_notes,
        factura_observations: data.factura_observations,
        deliveryDate: data.delivery_date,
        requestedDeliveryDateStart: data.requested_delivery_date_start,
        requestedDeliveryDateEnd: data.requested_delivery_date_end,
        effectiveDeliveryDateStart: data.effective_delivery_date_start,
        effectiveDeliveryDateEnd: data.effective_delivery_date_end,
        deliveryAddressId: data.delivery_address_id,
        deliveryStreet: data.delivery_street,
        deliveryNumber: data.delivery_number,
        deliveryComplement: data.delivery_complement,
        deliveryCity: data.delivery_city,
        deliveryState: data.delivery_state,
        deliveryComuna: data.delivery_comuna,
        rejectedReason: data.rejected_reason,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        salesConditionId: data.sales_condition_id,
        creation_lat: data.creation_lat,
        creation_lng: data.creation_lng,
        assigned_transportista: data.assigned_transportista,
      } as SalesRequest;

      return orderData;
    },
    enabled: !!salesRequestId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Fetch and cache the line items of a single sales request.
 *
 * `useSalesRequest` only loads the `sales_request` header row, so callers that
 * render the order-detail dialog (e.g. FacturasPage) must load the items
 * separately. Reuses the same query + mapping as the notas de venta list so
 * both dialogs display an identical item list.
 */
export const useSalesRequestItems = (salesRequestId: string | null) => {
  return useQuery({
    queryKey: ["salesRequestItems", salesRequestId],
    queryFn: async (): Promise<SalesRequestItem[]> => {
      if (!salesRequestId) {
        throw new Error("Sales request ID is required");
      }

      const itemsMap = await fetchSalesRequestItemsUtil([
        { id: salesRequestId } as SalesRequest,
      ]);

      return itemsMap[salesRequestId] ?? [];
    },
    enabled: !!salesRequestId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook to invalidate sales request queries
 */
export const useInvalidateSalesRequest = () => {
  const queryClient = useQueryClient();

  const invalidateSalesRequest = (salesRequestId?: string) => {
    if (salesRequestId) {
      // Invalidate specific sales request
      queryClient.invalidateQueries({
        queryKey: ["salesRequest", salesRequestId],
      });
    } else {
      // Invalidate all sales request queries
      queryClient.invalidateQueries({
        queryKey: ["salesRequest"],
      });
    }
  };

  const prefetchSalesRequest = (salesRequestId: string) => {
    queryClient.prefetchQuery({
      queryKey: ["salesRequest", salesRequestId],
      queryFn: async (): Promise<SalesRequest> => {
        const { data, error } = await supabase
          .from("sales_request")
          .select("*")
          .eq("id", salesRequestId)
          .single();

        if (error) {
          throw new Error(`Failed to fetch sales request: ${error.message}`);
        }

        return {
          id: data.id,
          clientId: data.client_id,
          createdBy: data.created_by,
          approvedRejectedBy: data.approved_rejected_by,
          status: data.status,
          paid: data.paid,
          paymentTerms: data.payment_terms,
          paymentType: data.payment_type,
          paymentDueDate: data.payment_due_date,
          totalAmount: data.total_amount,
          price_with_iva: data.price_with_iva,
          comments: data.comments,
          internal_notes: data.internal_notes,
          factura_observations: data.factura_observations,
          deliveryDate: data.delivery_date,
          requestedDeliveryDateStart: data.requested_delivery_date_start,
          requestedDeliveryDateEnd: data.requested_delivery_date_end,
          effectiveDeliveryDateStart: data.effective_delivery_date_start,
          effectiveDeliveryDateEnd: data.effective_delivery_date_end,
          deliveryAddressId: data.delivery_address_id,
          deliveryStreet: data.delivery_street,
          deliveryNumber: data.delivery_number,
          deliveryComplement: data.delivery_complement,
          deliveryCity: data.delivery_city,
          deliveryState: data.delivery_state,
          deliveryComuna: data.delivery_comuna,
          rejectedReason: data.rejected_reason,
          createdAt: data.created_at,
          updatedAt: data.updated_at,
          salesConditionId: data.sales_condition_id,
          creation_lat: data.creation_lat,
          creation_lng: data.creation_lng,
          assigned_transportista: data.assigned_transportista,
        } as SalesRequest;
      },
      staleTime: 5 * 60 * 1000,
    });
  };

  return {
    invalidateSalesRequest,
    prefetchSalesRequest,
  };
};
